// src/context/cartContext.jsx - FIXED TO EXPOSE GST DATA

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchCart, 
  addToCart, 
  updateCartItem, 
  removeFromCart, 
  clearCart,
  syncCart,
  addToGuestCart,
  updateGuestCartItem,
  removeFromGuestCart,
  clearGuestCart,
  loadGuestCartFromStorage,
  setIsAuthenticated
} from '../store/slices/cartSlice';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(state => state.auth);
  const cartState = useSelector(state => state.cart);
  
  const [syncAttempted, setSyncAttempted] = useState(false);
  
  // Load guest cart from localStorage on mount
  useEffect(() => {
    // console.log('🔄 CartContext: Loading guest cart from storage');
    dispatch(loadGuestCartFromStorage());
  }, [dispatch]);
  
  // Update cart authentication state
  useEffect(() => {
    // console.log('🔐 CartContext: Auth state changed:', isAuthenticated);
    dispatch(setIsAuthenticated(isAuthenticated));
  }, [isAuthenticated, dispatch]);
  
  // Sync cart after login
  useEffect(() => {
    const syncGuestCartAfterLogin = async () => {
      if (isAuthenticated && !syncAttempted) {
        // console.log('🔄 CartContext: User logged in, attempting sync...');
        
        // Get guest cart from localStorage
        const storedGuestCart = localStorage.getItem('guestCart');
        
        if (storedGuestCart) {
          try {
            const guestCart = JSON.parse(storedGuestCart);
            
            if (guestCart.items && guestCart.items.length > 0) {
              // console.log(`🔄 Syncing ${guestCart.items.length} guest items...`);
              
              await dispatch(syncCart(guestCart)).unwrap();
              
              // console.log('✅ Cart sync completed successfully');
              
              // Clear guest cart from localStorage after successful sync
              localStorage.removeItem('guestCart');
            } else {
              // console.log('📭 Guest cart is empty, fetching user cart');
              await dispatch(fetchCart()).unwrap();
            }
          } catch (error) {
            console.error('❌ Cart sync failed:', error);
            await dispatch(fetchCart()).unwrap();
          }
        } else {
          // console.log('📭 No guest cart found, fetching user cart');
          await dispatch(fetchCart()).unwrap();
        }
        
        setSyncAttempted(true);
      } else if (isAuthenticated && syncAttempted) {
        // console.log('🔄 Refreshing authenticated cart');
        dispatch(fetchCart());
      } else if (!isAuthenticated) {
        setSyncAttempted(false);
      }
    };
    
    syncGuestCartAfterLogin();
  }, [isAuthenticated, syncAttempted, dispatch]);
  
  // ✅ CRITICAL FIX: Build complete cart object with GST data
  const getEnhancedCart = () => {
    const baseCart = isAuthenticated ? cartState.cart : cartState.guestCart;
    
    // ✅ Add GST calculation data from Redux state
    return {
      ...baseCart,
      // Items
      items: baseCart.items || [],
      
      // Totals from Redux state
      subtotal: cartState.subtotal || 0,
      discount: cartState.discount || 0,
      bulkDiscount: baseCart.bulkDiscount || 0,
      
      // ✅ GST DATA - This is what was missing!
      gstCalculation: {
        cgst: cartState.cgst || 0,
        sgst: cartState.sgst || 0,
        igst: cartState.igst || 0,
        totalGst: isAuthenticated
          ? (baseCart.gstCalculation?.totalGst || cartState.gstAmount || 0)
          : cartState.gstAmount || 0
      },
      
      // Shipping
      shipping: {
        cost: cartState.shippingCost || 0,
        freeShippingEligible: cartState.shippingCost === 0,
        minOrderForFreeShipping: 5000,
        ...(baseCart.shipping || {})
      },
      
      // Other cart properties
      totalMrp: baseCart.totalMrp,
      totalItems: baseCart.totalItems,
      deliveryPincode: baseCart.deliveryPincode,
      deliveryState: baseCart.deliveryState
    };
  };
  
  const cart = getEnhancedCart();
  const itemCount = cartState.itemCount || 0;
  const totalAmount = cartState.totalAmount || 0;
  
  // Add to cart with complete product data
  const addItem = async (productData) => {
    // console.log('🛒 CartContext: Adding item', productData);
    
    // Ensure ALL required fields are present
    const completeProductData = {
      // Required fields
      productId: productData.productId,
      name: productData.name,
      price: productData.price || productData.sellingPrice,
      mrp: productData.mrp,
      quantity: productData.quantity || 1,
      category: productData.category,
      subCategory: productData.subCategory,
      material: productData.material,
      color: productData.color,
      
      // Images
      imageUrl: productData.imageUrl || (productData.images?.[0]?.url),
      thumbnailUrl: productData.thumbnailUrl || (productData.images?.[0]?.thumbnailUrl) || (productData.images?.[0]?.url),
      
      // Stock information
      stock: productData.stock || 0,
      stockAvailable: productData.stockAvailable || productData.stock || 0,
      isInStock: productData.isInStock !== undefined ? productData.isInStock : (productData.stock > 0),
      stockStatus: productData.stockStatus || (productData.stock === 0 ? 'OUT_OF_STOCK' : productData.stock <= 10 ? 'LOW_STOCK' : 'IN_STOCK'),
      
      // Brand and manufacturer
      brand: productData.brand,
      manufacturer: productData.manufacturer,
      
      // Physical specifications
      thickness: productData.thickness,
      weight: productData.weight,
      gsm: productData.gsm,
      dimensions: productData.dimensions,
      coverageArea: productData.coverageArea,
      piecesPerPack: productData.piecesPerPack || 1,
      rollLength: productData.rollLength,
      rollWeight: productData.rollWeight,
      
      // Order constraints
      minOrderQuantity: productData.minOrderQuantity || 1,
      maxOrderQuantity: productData.maxOrderQuantity,
      
      // Flags
      isEcoFriendly: productData.isEcoFriendly || false,
      isBulkItem: productData.isBulkItem || false,
      requiresSpecialHandling: productData.requiresSpecialHandling || false,
      
      // Delivery
      estimatedDeliveryDays: productData.estimatedDeliveryDays || 3,
      deliveryMessage: productData.deliveryMessage || 'Delivery in 2-3 business days',
      
      // Optional fields
      customerNotes: productData.customerNotes || '',
      urgency: productData.urgency || 'WEEKLY',
      sizeLabel: productData.sizeLabel || null,
      gstRate: productData.gstRate ?? 18
    };
    
    if (isAuthenticated) {
      // console.log('🔵 Authenticated: Adding to backend cart');
      // For authenticated users, send minimal data to backend
      const backendPayload = {
        productId: completeProductData.productId,
        quantity: completeProductData.quantity,
        color: completeProductData.color,
        dimensions: completeProductData.dimensions,
        customerNotes: completeProductData.customerNotes,
        urgency: completeProductData.urgency,
        sizeLabel: completeProductData.sizeLabel,
        gstRate: completeProductData.gstRate ?? 18,
      };
      
      return dispatch(addToCart(backendPayload)).unwrap();
    } else {
      // console.log('👤 Guest: Adding to local cart with complete data');
      // For guests, store complete data locally
      return dispatch(addToGuestCart(completeProductData));
    }
  };
  
  const updateItem = async ({ productId, quantity, color, dimensions, sizeLabel }) => {
    // console.log('📝 CartContext: Updating item', { productId, quantity, color });
    
    if (isAuthenticated) {
      return dispatch(updateCartItem({ productId, quantity, color, dimensions, sizeLabel })).unwrap();
    } else {
      return dispatch(updateGuestCartItem({ productId, quantity, color, sizeLabel }));
    }
  };
  
  const removeItem = async (productId, color = null, sizeLabel = null) => {
    // console.log('🗑️ CartContext: Removing item', { productId, color, sizeLabel });
    
    if (isAuthenticated) {
      return dispatch(removeFromCart({ productId, color, sizeLabel })).unwrap();
    } else {
      return dispatch(removeFromGuestCart({ productId, color, sizeLabel })); 
    }
  };
  
  const emptyCart = async () => {
    // console.log('🧹 CartContext: Emptying cart');
    
    if (isAuthenticated) {
      return dispatch(clearCart()).unwrap();
    } else {
      return dispatch(clearGuestCart());
    }
  };
  
  // Force refresh cart from backend
  const refreshCart = async () => {
    if (isAuthenticated) {
      // console.log('🔄 CartContext: Force refreshing cart from backend');
      return dispatch(fetchCart()).unwrap();
    }
  };
  
  // ✅ FIXED: Expose complete cart data including GST
  const value = {
    cart, // Now includes gstCalculation!
    itemCount,
    totalAmount,
    loading: cartState.loading,
    error: cartState.error,
    isAuthenticated,
    addItem,
    updateItem,
    removeItem,
    emptyCart,
    refreshCart
  };
  
  // console.log('💰 CartContext Value:', {
  //   itemCount,
  //   totalAmount,
  //   subtotal: cart.subtotal,
  //   gstTotal: cart.gstCalculation?.totalGst,
  //   hasGstData: !!cart.gstCalculation
  // });
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;